import { useEffect, useState } from 'react';

import AppPage from 'layouts/AppPage/AppPage';
import { Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { OverflowTabs } from 'components/Tabs/Tabs';

import { messagePopUp } from 'api/app-slice';
import { getBrands, selectBrands } from 'api/brands-slice';
import { useLazyGetCheckRenewalsQuery } from 'api/check-renewals-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { useGetRenewalConfigsQuery } from 'api/renewals-config-api';
import { useGetCompanyUsersQuery } from 'api/user-api';
import { selectUser } from 'api/user-slice';

import styles from './Renewals.module.scss';
import { RenewalChecksTab } from './tabs/RenewalChecksTab/RenewalChecksTab';
import { RenewalResultsTab } from './tabs/RenewalResultsTab/RenewalResultsTab';
import { RenewalsConfigurationTab } from './tabs/RenewalsConfigurationTab/RenewalsConfigurationTab';
import { RenewalsEmployeesTab } from './tabs/RenewalsEmployeesTab/RenewalsEmployeesTab';

const Renewals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tab: targetTab } = useParams();
  const { t } = useTranslation();

  const companyChecks = useSelector(selectCompanyChecks);
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const brands = useSelector(selectBrands);
  const [tab, setTab] = useState(targetTab);

  const [getRenewals] = useLazyGetCheckRenewalsQuery();

  const { data: renewalConfigsData } = useGetRenewalConfigsQuery({});
  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id);

  const [renewalConfigs, setRenewalConfigs] = useState();
  const [loading, setLoading] = useState(false);

  const handleMessagePopUp = (text, state, hide = true) =>
    dispatch(messagePopUp({ text, state, hide }));

  useEffect(() => {
    dispatch(getBrands());
  }, []);

  useEffect(() => {
    setRenewalConfigs(renewalConfigsData);
  }, [renewalConfigsData]);

  useEffect(() => {
    if (renewalConfigs?.length === 0) {
      setTab('configuration');
    } else {
      setTab(targetTab);
    }
  }, [targetTab, renewalConfigs]);

  const setTabPath = key => {
    setTab(key);
    navigate(`/renewals/${key}`);
  };

  return (
    <AppPage loading={loading}>
      <div className={styles.root}>
        <AppPage.Header title={t('renewals.title')} subtitle={t('renewals.subtitle')} />
        <OverflowTabs
          defaultActiveKey="upcoming"
          activeKey={tab}
          onSelect={setTabPath}
          className="mt-12 mx-12">
          <Tab
            eventKey="upcoming"
            title={t('renewals.tabs.upcoming')}
            disabled={renewalConfigs?.length === 0}>
            <RenewalChecksTab
              renewalConfigs={renewalConfigs}
              company={company}
              messagePopUp={handleMessagePopUp}
              setLoading={setLoading}
              getRenewals={getRenewals}
            />
          </Tab>
          <Tab eventKey="configuration" title={t('renewals.tabs.configuration.title')}>
            <RenewalsConfigurationTab
              companyChecks={companyChecks}
              renewalConfigs={renewalConfigs}
              updateLocalRenewalConfigs={setRenewalConfigs}
              companyUsers={companyUsers}
              brands={brands}
              setLoading={setLoading}
            />
          </Tab>
          <Tab
            eventKey="employees"
            title={t('renewals.tabs.employees')}
            disabled={renewalConfigs?.length === 0}>
            <RenewalsEmployeesTab
              company={company}
              setLoading={setLoading}
              messagePopUp={handleMessagePopUp}
            />
          </Tab>
          <Tab
            eventKey="results"
            title={t('renewals.tabs.results')}
            disabled={renewalConfigs?.length === 0}>
            <RenewalResultsTab company={company} setLoading={setLoading} />
          </Tab>
        </OverflowTabs>
      </div>
    </AppPage>
  );
};

export default Renewals;
